/**
 * DiscountsPage component
 * @returns <DiscountsPage />
 */

import TitleAndButton from "../components/TitleAndButton";
import Breadcrumbs from "../components/Breadcrumbs";
import StoreGridContainer from "../components/StoreGridContainer";
import FilterContainer from "../components/FilterContainer";
import FiltersContainer from "../components/FiltersContainer";
import BoxWithBackgroundContainer from "../components/BoxWithBackgroundContainer";
import InputRadio from "../components/InputRadio";
import Accordion from "../components/Accordion";
import Drawer from "../components/Drawer";
import Button from "../components/Button";
import Loader from "../components/Loader";
import LegalFooter from "../components/LegalFooter";
import NoDiscountsBanner from "../components/NoDiscountsBanner";

import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
    useGetAllAccesoriesQuery,
    useGetAllMerchandisingQuery,
    useGetLegalesQuery,
} from "../redux/apiSlice";
import { AnimatePresence } from "framer-motion";

const DiscountsPage = () => {
    const [searchParams, setSearchParams] = useSearchParams();

    const {
        data: accessories,
        isLoading: accessoriesIsLoading,
        isSuccess: accessoriesIsSuccess,
        isError: accessoriesIsError,
    } = useGetAllAccesoriesQuery();

    const {
        data: merchandisingItems,
        isLoading: merchandisingIsLoading,
        isSuccess: merchandisingIsSuccess,
        isError: merchandisingIsError,
    } = useGetAllMerchandisingQuery();

    const {
        data: legals,
        isSuccess: legalsIsSuccess,
    } = useGetLegalesQuery();

    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [selectedType, setSelectedType] = useState(searchParams.get("categoria") || "all");

    const hasDiscountPrice = (item) =>
        item.price_discount !== null &&
        item.price_discount !== undefined &&
        item.price_discount !== "";

    let discountedItems = [];
    if (accessoriesIsSuccess && merchandisingIsSuccess) {
        const discountedAccessories = accessories
            .filter(hasDiscountPrice)
            .map((item) => ({
                ...item,
                url: `/accesorios/${item.slug}`,
            }));

        const discountedMerchandising = merchandisingItems
            .filter(hasDiscountPrice)
            .map((item) => ({
                ...item,
                url: `/merchandising/${item.slug}`,
            }));

        discountedItems = [...discountedAccessories, ...discountedMerchandising];
    }

    const filteredItems =
        selectedType === "all"
            ? discountedItems
            : discountedItems.filter((item) => item.type === selectedType);

    const typeChangeHandler = (e) => {
        const type = e.target.value;
        setSelectedType(type);
        
        // Update URL
        const newParams = new URLSearchParams(searchParams);
        if (type && type !== "all") {
            newParams.set("categoria", type);
        } else {
            newParams.delete("categoria");
        }
        setSearchParams(newParams);
    };

    const toggleDrawer = () => {
        setIsDrawerOpen(!isDrawerOpen);
    };

    const filtersData = {
        title: "¿Qué descuentos estás buscando?",
        type: {
            title: "Producto",
            options: [
                {
                    type: "radio",
                    id: "discount-type-all",
                    label: "Todos",
                    name: "discount-type",
                    value: "all",
                },
                {
                    type: "radio",
                    id: "discount-type-accessories",
                    label: "Accesorios",
                    name: "discount-type",
                    value: "accessory",
                },
                {
                    type: "radio",
                    id: "discount-type-merchandising",
                    label: "Merchandising",
                    name: "discount-type",
                    value: "merchandise",
                },
            ],
        },
    };

    const typeInputList = filtersData.type.options.map((option, i) => (
        <InputRadio
            {...option}
            key={i}
            isChecked={selectedType === option.value}
            changed={typeChangeHandler}
        />
    ));

    const drawerFilters = [
        {
            label: "Producto",
            content: <>{typeInputList}</>,
        },
    ];

    const breadcrumbsProps = {
        links: [
            {
                link: "/",
                label: "Inicio",
            },
            {
                link: location.pathname,
                label: location.pathname.slice(1),
            },
        ],
    };

    const titleAndButtonProps = {
        title: "Descuentos online en <br> accesorios y merchandising",
        buttons: [],
        backgroundColor: "white",
        extraClass: "",
    };

    const legalContent = legalsIsSuccess
        ? [legals.accessories, legals.merchandise].filter(Boolean).join("<br/>")
        : "";

    const isLoading = accessoriesIsLoading || merchandisingIsLoading;
    const isError = accessoriesIsError || merchandisingIsError;
    const isSuccess = accessoriesIsSuccess && merchandisingIsSuccess;

    return (
        <>
            {isLoading && (
                <AnimatePresence>
                    <Loader />
                </AnimatePresence>
            )}

            {isSuccess && (
                <>
                    {discountedItems.length > 0 ? (
                        <>
                            <Breadcrumbs {...breadcrumbsProps} />

                            <TitleAndButton {...titleAndButtonProps} />

                            <StoreGridContainer>
                                <Button
                                    tag="link"
                                    text="Filtrar por"
                                    hasIcon={true}
                                    onClick={toggleDrawer}
                                />

                                <FiltersContainer>
                                    <h3>{filtersData.title}</h3>

                                    <form action="">
                                        <fieldset className="fieldset-1">
                                            <FilterContainer title={filtersData.type.title}>
                                                {typeInputList}
                                            </FilterContainer>
                                        </fieldset>
                                    </form>
                                </FiltersContainer>

                                <BoxWithBackgroundContainer
                                    items={filteredItems}
                                    extraClass="--store-version"
                                />

                                <Drawer
                                    title={"¿Qué descuentos estás buscando?"}
                                    content={
                                        <Accordion items={drawerFilters} type={"filters"} />
                                    }
                                    isOpen={isDrawerOpen}
                                    close={toggleDrawer}
                                />
                            </StoreGridContainer>
                        </>
                    ) : (
                        <NoDiscountsBanner />
                    )}
                </>
            )}

            {isError && <p>Hubo un error</p>}
        </>
    );
};

export default DiscountsPage;
